import { Box, Button, HStack, SimpleGrid, Text, VStack } from '@chakra-ui/react';
import type { ReactNode } from 'react';
import type { CreateActionDefinition } from '../../../../constants/createActions';
import { Glyph } from '../../../ui/Glyph';
import { gradientVariantForGlyph } from '../../../../theme/iconGradient';
import { MaterialIcon } from '../../../ui/MaterialIcon';
import { CreateTile } from './CreateTile';

interface CreateChooseStepProps {
  readonly actions: ReadonlyArray<CreateActionDefinition>;
  readonly onSelect: (definition: CreateActionDefinition) => void;
  /** "Bower on your phone": desktop only (on a phone you are already there). */
  readonly onOpenPhone?: () => void;
  readonly showShortcuts: boolean;
  /** A capture tile is creating its note: every choice waits. */
  readonly isCommitting?: boolean;
}

function Section({ title, children }: { readonly title: string; readonly children: ReactNode }) {
  return (
    <VStack align="stretch" spacing={2} role="group" aria-label={title}>
      <Text
        fontSize="2xs"
        fontWeight="semibold"
        letterSpacing="wider"
        textTransform="uppercase"
        color="text.secondary"
      >
        {title}
      </Text>
      {children}
    </VStack>
  );
}

/** Step 1: every type the destination can hold, then New collection, then the phone. */
export function CreateChooseStep({
  actions,
  onSelect,
  onOpenPhone,
  showShortcuts,
  isCommitting = false,
}: CreateChooseStepProps) {
  // One "Create" section in registry order: a second "Capture" heading next
  // to "Capture in the lab" read as two different things (David, 2026-10-03).
  const tiles = actions
    .filter((a) => a.action.kind !== 'collection')
    .map((a) => (
      <CreateTile
        key={a.id}
        id={a.id}
        label={a.label}
        description={a.description}
        icon={a.icon}
        shortcut={showShortcuts ? a.shortcut : undefined}
        onSelect={() => onSelect(a)}
        isDisabled={isCommitting}
      />
    ));
  const collection = actions.find((a) => a.action.kind === 'collection');
  const columns = 'repeat(auto-fit, minmax(132px, 1fr))';

  return (
    <VStack align="stretch" spacing={5} aria-busy={isCommitting}>
      <Section title="Create">
        <SimpleGrid templateColumns={columns} spacing={2.5}>
          {tiles}
        </SimpleGrid>
      </Section>
      {collection && (
        <Button
          variant="outline"
          justifyContent="flex-start"
          h="auto"
          py={3}
          leftIcon={
            <Glyph
              {...collection.icon}
              size="20px"
              gradient={gradientVariantForGlyph(collection.icon.name)}
              color="currentColor"
            />
          }
          data-testid="create-tile-collection"
          isDisabled={isCommitting}
          aria-keyshortcuts={showShortcuts ? collection.shortcut : undefined}
          onClick={() => onSelect(collection)}
        >
          <Text flex={1} textAlign="left">
            New collection
          </Text>
          {showShortcuts && (
            <Text fontSize="2xs" color="text.secondary" aria-hidden>
              {collection.shortcut}
            </Text>
          )}
        </Button>
      )}
      {onOpenPhone && (
        <Section title="Capture in the lab">
          <HStack
            as="button"
            type="button"
            onClick={onOpenPhone}
            data-testid="create-tile-phone"
            aria-keyshortcuts={showShortcuts ? 'M' : undefined}
            spacing={3}
            p={3}
            bg="brand.surface"
            border="1px solid"
            borderColor="brand.mutedBorder"
            borderRadius="xl"
            textAlign="left"
            _hover={{ bg: 'brand.muted' }}
          >
            <Box
              w={9}
              h={9}
              borderRadius="full"
              bg="brand.muted"
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexShrink={0}
            >
              <MaterialIcon
                name="smartphone"
                boxSize={5}
                gradient={gradientVariantForGlyph('smartphone')}
              />
            </Box>
            <Box flex={1}>
              <Text fontSize="sm" fontWeight="semibold" color="text.primary">
                Bower on your phone
              </Text>
              <Text fontSize="xs" color="text.secondary">
                Photos and voice notes from the bench
              </Text>
            </Box>
            <MaterialIcon name="arrow_forward" boxSize={5} color="text.secondary" />
          </HStack>
        </Section>
      )}
    </VStack>
  );
}
